import { useField } from 'formik';
import { useCallback, useMemo } from 'react';

import { DEFAULT_MANAGED_INTEGRATION_RELEASE_CHANNEL } from '@frontend/api';
import {
  Box,
  FormControl,
  FormLabel,
  Select,
} from '@frontend/legacy-components';

const RELEASE_CHANNEL_OPTIONS = [
  { label: 'Stable', value: 'stable' },
  { label: 'Development', value: 'dev' },
] as const;

/**
 * Release channel selector for Cloud Connector (ICC) setup. Mirrors the layout pattern of
 * {@link IccSetupAwsCredentials}: intended to sit in the same indented `VStack` as AWS fields.
 * Must render under a Formik `Form`.
 */
const IccSetupReleaseChannel = (): JSX.Element => {
  const [field, , helpers] = useField('releaseChannel');

  const resolvedReleaseChannel = useMemo(
    () =>
      field.value === 'stable' || field.value === 'dev'
        ? field.value
        : DEFAULT_MANAGED_INTEGRATION_RELEASE_CHANNEL,
    [field.value],
  );

  const selectedOption = useMemo(
    () =>
      RELEASE_CHANNEL_OPTIONS.find(
        ({ value }) => value === resolvedReleaseChannel,
      ) ?? null,
    [resolvedReleaseChannel],
  );

  const handleChange = useCallback(
    (e: { label: string; value: string }) => helpers.setValue(e?.value ?? ''),
    [helpers],
  );

  return (
    <FormControl isRequired>
      <FormLabel htmlFor="icc-setup-release-channel">Release channel</FormLabel>
      <Box width="100%" maxWidth="100%">
        <Select
          inputId="icc-setup-release-channel"
          name={field.name}
          onBlur={field.onBlur}
          isClearable={false}
          value={selectedOption}
          options={RELEASE_CHANNEL_OPTIONS}
          onChange={handleChange}
          placeholder="Select a release channel"
        />
      </Box>
    </FormControl>
  );
};

export default IccSetupReleaseChannel;
